import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faTimes, faCheck} from '@fortawesome/free-solid-svg-icons';

import {Modal, ModalHeader, ModalBody, ModalFooter} from 'Components/Modal';
import {Alert} from 'Components/Partials';
import {FormHandler, Input} from 'Components/Form';
import {PrimaryButton} from 'Components/Button';

import QuizQuestionApi from 'Services/Api/Exhibitor/QuizQuestion';

class UpdateQuizQuestion extends React.Component {
    /**
     * @var success
     * @type {string}
     */
    success = 'Your quiz question has been updated';

    /**
     * @method componentDidMount
     */
    componentDidMount = async () => {
        const {setForm, quizQuestion} = this.props;

        let form = {};

        if (quizQuestion) {
            form = {
                question: quizQuestion.question,
                answers: quizQuestion.answers
            };
        } else {
            form = {
                question: '',
                answers: null,
            };
        }

        setForm(form);
    }

    /**
     * @method handleSubmit
     * @param {Event} e
     */
    handleSubmit = async (e) => {
        e.preventDefault();

        const {form, setAlert, setWorking, exhibitor, quizQuestion, submitCallback} = this.props;

        setWorking(true);

        // Validate a correct answer has been set.
        if (form.answers) {
            let correct_answers = form.answers.filter(answer => answer.correct_answer === true);
            if (correct_answers.length !== 1) {
                setAlert('error', 'You must tick the correct answer.', []);
                setWorking(false);
                return false;
            }
        }

        // Submit
        let response = null;

        if (quizQuestion) {
            response = await QuizQuestionApi.patch(quizQuestion.id, form);
        } else {
            response = await QuizQuestionApi.post(null, {
                ...form,
                exhibitor_id: exhibitor.id
            });
        }

        // Handle Response
        if (!response.success) {
            setAlert('error', response.message, response.errors);
            setWorking(false);
            return false;
        } else {
            setAlert('success', this.success, {});
            submitCallback();
            this.props.onClose();
        }
    };

    /**
     * @method handleDelete
     */
    handleDelete = async () => {
        const {setAlert, setWorking, quizQuestion, submitCallback} = this.props;

        setWorking(true);

        let response = await QuizQuestionApi.delete(quizQuestion.id);

        setAlert('success', 'Item deleted', {});
        setWorking(false);

        submitCallback();

        this.props.onClose();
    };

    /**
     * @method addAnswer
     */
    addAnswer = () => {
        const {form, handleInput} = this.props;

        let new_answers = {};

        if (form.answers) {
            // https://stackoverflow.com/questions/4020796/finding-the-max-value-of-an-attribute-in-an-array-of-objects
            let last_key = Math.max.apply(Math, form.answers.map(function(o) { return o.key; }));

            last_key++;

            new_answers = [
                ...form.answers,
                {
                    key: last_key,
                    value: '',
                    correct_answer: false,
                }
            ];

        } else {
            new_answers = [
                {
                    key: 1,
                    value: '',
                    correct_answer: false,
                }
            ];
        }

        handleInput('answers', new_answers);
    }

    /**
     * @method updateAnswers
     * @param {string} key
     * @param {string} value
     * @param {boolean} correct_answer
     */
    updateAnswers = (key, value, correct_answer) => {
        const {form, handleInput} = this.props;

        let new_answers = form.answers.map((answer, i) => {
            if (answer.key === key) {
                return {
                    key: key,
                    value: value,
                    correct_answer,
                };
            } else {
                return answer;
            }
        })

        handleInput('answers', new_answers);
    }

    /**
     * @method removeAnswer
     * @param {string} i
     */
    removeAnswer = (i) => {
        const {form, handleInput} = this.props;
        const {answers} = form;

        answers.splice(i, 1);

        handleInput('answers', answers);
    };

    /**
     * @method correctAnswerQuestion
     * @param {object} correct_answer
     */
    correctAnswerQuestion = (correct_answer) => {
        const {form, handleInput} = this.props;

        let new_answers = form.answers.map((answer, i) => {
            if (answer.key === correct_answer.key) {
                return {
                    key: answer.key,
                    value: answer.value,
                    correct_answer: true,
                };
            } else {
                return {
                    key: answer.key,
                    value: answer.value,
                    correct_answer: false,
                };
            }
        })

        handleInput('answers', new_answers);
    }

    /**
     * @method render
     * @return {JSX.Element}
     */
    render () {
        const {form, handleInput, getFieldError, alert, working, quizQuestion} = this.props;

        return (
            <Modal>
                <ModalHeader onClose={this.props.onClose}>
                    {quizQuestion ? 'Update' : 'Submit'} your quiz question
                </ModalHeader>

                {(quizQuestion && quizQuestion.has_submissions) &&
                    <>
                        <ModalBody>
                            <p>
                                You can not update this quiz question as submissions have been made.
                            </p>
                        </ModalBody>

                        <ModalFooter alignment="center">
                            <PrimaryButton
                                text="Close"
                                onClick={this.props.onClose}
                                className="mr-2"
                            />
                        </ModalFooter>
                    </>
                }

                {(!quizQuestion || !quizQuestion.has_submissions) &&
                    <form onSubmit={e => this.handleSubmit(e)}>
                        <ModalBody>
                            {alert && (<Alert {...alert} />)}

                            <Input
                                containerClassName="my-4"
                                label="Question"
                                type="text"
                                value={form.question}
                                error={getFieldError('question')}
                                onChange={v => handleInput('question', v)}
                                labelColour="text-app-blue"
                            />

                            {form.answers && form.answers.map((answer, i) => {
                                return (
                                    <div className="grid grid-cols-10">
                                        <div
                                            className={`
                                                mt-4 flex items-center justify-center transition duration-200 transform hover:scale-110 cursor-pointer
                                                ${answer.correct_answer ? 'text-green-500' : 'text-gray-700'}
                                            `}
                                            onClick={() => this.correctAnswerQuestion(answer)}
                                        >
                                            <FontAwesomeIcon icon={faCheck} size="lg" />
                                        </div>

                                        <div className="col-span-8">
                                            <Input
                                                containerClassName="my-4"
                                                label={"Answer " + answer.key}
                                                type="text"
                                                value={answer.value}
                                                onChange={value => this.updateAnswers(answer.key, value, answer.correct_answer)}
                                                labelColour="text-app-blue"
                                            />
                                        </div>

                                        <div
                                            className="mt-4 flex text-red-500 items-center justify-center transition duration-200 transform hover:scale-110 cursor-pointer"
                                            onClick={() => this.removeAnswer(i)}
                                        >
                                            <FontAwesomeIcon icon={faTimes} size="lg" />
                                        </div>
                                    </div>
                                );
                            })}

                            <div className="text-center mt-4">
                                <PrimaryButton
                                    text="Add An Answer"
                                    onClick={() => this.addAnswer()}
                                />
                            </div>
                        </ModalBody>

                        <ModalFooter alignment="center">
                            <PrimaryButton
                                text="Close"
                                onClick={this.props.onClose}
                                className="mr-2"
                            />

                            {quizQuestion &&
                                <PrimaryButton
                                    className="ml-2"
                                    text="Delete"
                                    onClick={this.handleDelete}
                                    disabled={working}
                                    working={working}
                                />
                            }

                            <PrimaryButton
                                className="ml-2"
                                text={quizQuestion ? 'Update' : 'Submit'}
                                disabled={working}
                                working={working}
                            />
                        </ModalFooter>
                    </form>
                }
            </Modal>
        )
    }
}

export default FormHandler(UpdateQuizQuestion);
